<script setup>
const { loading } = useLoading()
const route = useRoute()
const router = useRouter()
const { getProducersForSlider, getCategories, getArticle } = useDatabase()
const auth = useAuth()

const [producers, categories, news] = await Promise.all([
  getProducersForSlider(),
  getCategories({is_public: 'true'}).then(data => {
    const map = {};
    const result = [];

    // データを map に格納し、同時に result に最上位の要素を追加
    data.forEach(item => {
      map[item.id] = { ...item };
      if (item.parent_id === null) {
        result.push(map[item.id]);
      }
    });

    // 再帰関数
    function buildHierarchy(item) {
      if (item.parent_id !== null) {
        if (map[item.parent_id]) {
          const parent = map[item.parent_id];
          parent.children = parent.children || [];
          parent.children.push(item);

          // 親の _count に子の _count を加算
          parent._count.producers += item._count.producers;
        }
      }

      if (item.children?.length) {
        item.children.forEach(child => buildHierarchy(child));
      }
    }

    // 各要素に対して再帰処理を実行
    data.forEach(item => buildHierarchy(item));
    return result;
  }),
  getArticle()
])

const genreMenu = ref(false)

const onClickSection = async (section) => {
  const swiper = document.querySelector('.swiper-contents')
  const swiperHeigh = swiper?.clientHeight || 0
  scrollTo({top: swiperHeigh, behavior: 'smooth'})

  if (section != route.query.l) {
    router.push({ query: {l: section} })
  }
}

const topFirstCategory = (producer) => {
  const categories = producer.categories
  if (producer.top_category) {
    return categories.find(v => v.id == producer.top_category)
  } else if (producer.categories) {
    const topCategories = categories.filter(v => !v.parent_id)
    return producer.top_category || topCategories[0]
  }
}

const changedMenu = (value) => {
  if (!value) setTimeout(() => genreMenu.value = value, 10);
}
</script>

<template>
  <div
    v-if="producers?.primary && producers?.secondary"
    class="swiper-contents"
  >
    <client-only>
      <default-ui-swiper-home
        class="mb-4"
        :items="producers.primary"
      >
        <template #default="{item, i}">
          <NuxtLink
            :to="'/producer/'+item.cuid"
            class="swiper-slide-item !border-0"
          >
            <div class="aspect-4/3">
              <NuxtImg
                class="w-full h-full object-cover rounded-2xl"
                width="100%"
                height="100%"
                :src="item.image_files.length ? encodeURI(item.image_files[0].url) : '/noimage.png'"
              />
            </div>
            <span
              v-if="topFirstCategory(item)"
              class="swiper-slide-item-category"
            >{{ topFirstCategory(item).name }}</span>
            <p class="swiper-slide-item-description">{{ item.prefecture }}/{{ item.address1 }}</p>
          </NuxtLink>
        </template>
      </default-ui-swiper-home>
      <default-ui-swiper-home
        reverse
        class="mb-4"
        :items="producers.secondary"
      >
        <template #default="{item, i}">
          <NuxtLink
            :to="'/producer/'+item.cuid"
            class="swiper-slide-item !border-0"
          >
            <div class="aspect-4/3">
              <NuxtImg
                class="w-full h-full object-cover rounded-2xl"
                width="100%"
                height="100%"
                :src="item.image_files.length ? encodeURI(item.image_files[0].url) : '/noimage.png'"
              />
            </div>
            <span
              v-if="topFirstCategory(item)"
              class="swiper-slide-item-category"
            >{{ topFirstCategory(item).name }}</span>
            <p class="swiper-slide-item-description">{{ item.prefecture }}/{{ item.address1 }}</p>
          </NuxtLink>
        </template>
      </default-ui-swiper-home>
    </client-only>

    <div
      v-if="news"
      class="z-10 block my-8"
    >
      <span class="w-full max-w-[1200px] mx-auto flex items-center gap-x-3  px-3 py-3 bg-yellow-100/50 hover:bg-yellow-100 transition-all duration-300">
        <icon icon="mdi:info-circle-outline" class="text-yellow-500" width="24" />

        <div class="flex gap-x-2 flex-col sm:flex-row text-left">
          <span
            class="badge w-fit !text-white"
            :style="{backgroundColor: news.category?.color}"
          >{{ news.category?.name }}</span>
          <NuxtLink
            :to="`/article/${news.id}`"
            class=""
          >
            <p class="flex-1 text-left">{{ news.title }}</p>
          </NuxtLink>
        </div>
      </span>
    </div>
  </div>

  <default-ui-main>
    <div class="max-w-[1200px] mx-auto">
      <div
        id="product-list"
        class="grid grid-cols-4 border-x divide-x border-zinc divide-zinc mb-12"
      >
        <button type="button" class="pt-2 pb-4 text-center relative" @click="onClickSection('all')">
          <div class="px-2 -mt-3 max-w-20 max-h-20 w-full h-full flex items-center mx-auto">
            <NuxtImg src="/icon/list.png" class="max-w-24 w-full mx-auto" />
          </div>
          <span
            class="font-bold text-xs sm:text-base"
            :class="{'text-primary': !route.query.c && (!route.query.l || route.query.l == 'all')}"
          >すべて表示</span>
          <div
            v-if="!route.query.c && (!route.query.l || route.query.l == 'all')"
            class="hidden sm:inline-block absolute bg-primary w-3 h-3 rounded-full left-1/2 -translate-x-1/2 -bottom-5"
          ></div>
        </button>
        <button type="button" class="pt-2 pb-4 text-center relative" @click="onClickSection('newest')">
          <div class="px-2 -mt-3 max-w-20 max-h-20 w-full h-full flex items-center mx-auto">
            <NuxtImg src="/icon/new.png" class="max-w-24 w-full mx-auto" />
          </div>
          <span
            class="font-bold text-xs sm:text-base"
            :class="{'text-primary': route.query.l == 'newest'}"
          >新着生産者</span>
          <div
            v-if="route.query.l == 'newest'"
            class="hidden sm:inline-block absolute bg-primary w-3 h-3 rounded-full left-1/2 -translate-x-1/2 -bottom-5"
          ></div>
        </button>
        <button type="button" class="pt-2 pb-4 text-center relative" @click="onClickSection('popularity')">
          <div class="px-2 -mt-3 max-w-20 max-h-20 w-full h-full flex items-center mx-auto">
            <NuxtImg src="/icon/crown.png" class="max-w-24 w-full mx-auto" />
          </div>
          <span
            class="font-bold text-xs sm:text-base"
            :class="{'text-primary': route.query.l == 'popularity'}"
          >人気ランキング</span>
          <div
            v-if="route.query.l == 'popularity'"
            class="hidden sm:inline-block absolute bg-primary w-3 h-3 rounded-full left-1/2 -translate-x-1/2 -bottom-5"
          ></div>
        </button>
        <default-ui-menu :model-value="genreMenu" @update:model-value="changedMenu">
          <template #activator>
            <button type="button" class="pt-2 pb-4 text-center w-full h-full" @click="genreMenu = !genreMenu">
              <div class="px-2 -mt-3 max-w-20 max-h-20 w-full h-full flex items-center mx-auto">
                <NuxtImg src="/icon/stones.png" class="max-w-24 w-full mx-auto" />
              </div>
              <span
                class="font-bold text-xs sm:text-base"
                :class="{'text-primary': !!route.query.c}"
              >カテゴリー</span>
            </button>
            <div
              v-if="!route.query.l && route.query.c"
              class="hidden sm:inline-block absolute bg-primary w-3 h-3 rounded-full left-1/2 -translate-x-1/2 -bottom-5"
            ></div>
          </template>

          <div class="p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-3 gap-2 w-[300px] sm:w-[500px]">
            <template v-for="(parent, p) of categories">
              <div
                v-if="parent._count.producers > 0"
                class=""
              >
                <NuxtLink
                  :to="{
                    path: '/',
                    query: { c: parent.id }
                  }"
                  class="block px-2 pb-1 mb-1 text-zinc text-center border-b border-b-zinc"
                >{{ parent.name }}</NuxtLink>

                <template v-for="(child, c) of (parent?.children || [])">
                  <NuxtLink
                    v-if="child._count.producers > 0"
                    :to="{
                      path: '/',
                      query: { c: child.id }
                    }"
                    class="px-3 py-2 text-lg flex justify-between items-center hover:bg-zinc-100 hover:text-primary max-w-full leading-tight"
                  >
                    {{ child.name }}
                    <icon icon="mdi:menu-right" width="22" />
                  </NuxtLink>
                </template>
              </div>
            </template>
          </div>
        </default-ui-menu>
      </div>


      <default-page-list />
    </div>
  </default-ui-main>
</template>